import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';

import StandardModal from 'modals/StandardModal';
import Spinner from 'components/Spinner';
// components/Paginator (the .js file) rather than components/Paginator/Paginator
// (the .jsx one): this is the paginator every other paged view uses --
// DataTableModal, the portfolio tables -- with caret controls, an ellipsis for
// long ranges and its own stylesheet. The .jsx one renders unstyled boxes and a
// "Page x of y" caption that appears nowhere else in the app.
import Paginator from 'components/Paginator';
import {
	useCertificatesOfOccupancy,
	SOURCE_BIS,
	SOURCE_DOBNOW,
} from 'data/repositories/certificatesOfOccupancyRepository';

import CertificatesOfOccupancyTable from './CertificatesOfOccupancyTable';
import './CertificatesOfOccupancyModal.css';

/** The two tabs, in the order the eras run. */
const TABS = [
	{ source: SOURCE_BIS, label: 'BIS', hint: 'Issued before ~March 2021' },
	{ source: SOURCE_DOBNOW, label: 'DOB NOW', hint: 'Issued ~2021 onward' },
];

/** How long to wait after a keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 400;

/**
 * CertificatesOfOccupancyModal
 *
 * The Quick Links popup: the Certificates of Occupancy we hold for the current
 * property, one tab per source.
 *
 * Two tabs rather than two modals because a building almost always has
 * certificates in one source and not the other -- BIS covers the era before
 * roughly March 2021, DOB NOW everything after -- so a reader who opens the
 * wrong one needs to reach the other without closing and re-opening the
 * dropdown.
 *
 * Reads only. The rows come from the two cache tables the scrapers fill; the
 * document link goes to DOB's own copy in a new tab, so nothing is proxied or
 * downloaded through us.
 *
 * @param {object} props
 * @param {object} props.options Modal options; `source` selects the opening tab.
 * @returns {JSX.Element}
 */
const CertificatesOfOccupancyModal = ({ options }) => {
	const propertyData = useSelector((state) => state.property.propertyData);
	const propertyId = propertyData?.id;

	const [source, setSource] = useState(options?.source || SOURCE_BIS);
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');

	const {
		bisCertificates,
		bisCertificatesPagination,
		isLoadingBisCertificates,
		bisCertificatesError,
		dobNowCertificates,
		dobNowCertificatesPagination,
		isLoadingDobNowCertificates,
		dobNowCertificatesError,
		actions,
	} = useCertificatesOfOccupancy();

	const isBis = source === SOURCE_BIS;
	const rows = isBis ? bisCertificates : dobNowCertificates;
	const pagination = isBis ? bisCertificatesPagination : dobNowCertificatesPagination;
	const isLoading = isBis ? isLoadingBisCertificates : isLoadingDobNowCertificates;
	const error = isBis ? bisCertificatesError : dobNowCertificatesError;

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (!propertyId) return;

		const fetch = isBis ? actions.getBisCertificates : actions.getDobNowCertificates;

		fetch({ propertyId, page, filter: search });
	}, [propertyId, source, page, search]);

	// A new source or a new search term is a new result set, so the page the
	// reader was on no longer refers to anything.
	const onSourceChange = (nextSource) => {
		if (nextSource === source) return;
		setPage(1);
		setSource(nextSource);
	};

	const debouncedSearch = useMemo(
		() =>
			debounce((value) => {
				setPage(1);
				setSearch(value);
			}, SEARCH_DEBOUNCE_MS),
		[],
	);

	useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

	const total = pagination?.total ?? 0;
	const totalPages = pagination?.lastPage ?? pagination?.last_page ?? 1;

	return (
		<StandardModal
			title="Certificates of Occupancy"
			size="lg"
			closeOnOutsideClick
		>
			<div className="cofo-modal">
				<ul className="nav nav-tabs cofo-tabs">
					{TABS.map((tab) => (
						<li className="nav-item" key={tab.source}>
							<button
								type="button"
								className={`nav-link ${tab.source === source ? 'active' : ''}`}
								onClick={() => onSourceChange(tab.source)}
								title={tab.hint}
							>
								{tab.label}
							</button>
						</li>
					))}
				</ul>

				<div className="cofo-controls">
					<input
						type="text"
						className="form-control cofo-search"
						placeholder="Search certificates"
						onChange={(event) => debouncedSearch(event.target.value)}
					/>
					{!isLoading && <span className="cofo-count text-muted">{total} found</span>}
				</div>

				<div className="cofo-table-wrapper">
					{isLoading && <Spinner />}

					{!isLoading && error && (
						<p className="cofo-error text-danger">
							These certificates could not be loaded. Please try again.
						</p>
					)}

					{!isLoading && !error && (
						<CertificatesOfOccupancyTable
							source={source}
							rows={rows}
							isLoading={isLoading}
						/>
					)}
				</div>

				<Paginator
					currentPage={page}
					totalPages={totalPages}
					setPage={setPage}
				/>
			</div>
		</StandardModal>
	);
};

CertificatesOfOccupancyModal.propTypes = {
	options: PropTypes.shape({
		source: PropTypes.oneOf([SOURCE_BIS, SOURCE_DOBNOW]),
	}),
};

CertificatesOfOccupancyModal.defaultProps = {
	options: {},
};

export default CertificatesOfOccupancyModal;
